import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Monitor,
  Network,
  Loader2,
  ChevronRight,
  AlertCircle,
  AlertTriangle,
  Server,
  History,
  Globe,
  RefreshCw,
  Link2,
  Wifi,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleTrigger, CollapsibleContent, useCollapsibleContext } from '@/components/ui/collapsible';
import { StationCard } from '@/components/StationCard';
import { useClientContext } from '../ClientContext';
import { useClientStations, useClientCard, useWaitingStations, useAssignStation } from '@/features/legacy/hooks/useStations';
import type { Station, LinuxServer } from '@/api/legacy';
import { StationBindingSheet } from '../components/StationBindingSheet';
import { parseHtmlToReact } from '@/lib/html-parser';

interface StationWithName extends Station {
  displayName: string;
}

function CollapsibleChevron() {
  const { open } = useCollapsibleContext();
  return <ChevronRight className={`h-3 w-3 transition-transform ${open ? 'rotate-90' : ''}`} />;
}

function LinuxServerCard({ server }: { server: LinuxServer }) {
  const isUp = server.inet === 1;
  const isDown = server.inet === 0;
  const isNetmapOnly = server.inet === -1;
  const hasCritical = !!server.critical?.trim() || !!server.card?.includes('S.M.A.R.T.');

  return (
    <div
      className={`w-full p-3 rounded-lg border text-left ${
        isDown || hasCritical
          ? 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-900'
          : isUp
          ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-900'
          : 'bg-card border-border'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Server className={`h-4 w-4 shrink-0 ${isDown ? 'text-red-500' : isUp ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`} />
          <span className="font-medium text-sm truncate">{server.server}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-2">
        {isUp && (
          <Badge variant="outline" className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700">
            Работает
          </Badge>
        )}
        {isDown && (
          <Badge variant="destructive" className="text-xs">
            Не работает
          </Badge>
        )}
        {isNetmapOnly && (
          <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-800">
            <Globe className="h-3 w-3 mr-1" />
            Netmap
          </Badge>
        )}
        {server.ISP && (
          <Badge variant="outline" className="text-xs">
            {server.ISP}
          </Badge>
        )}
        {hasCritical && (
          <Badge variant="outline" className="text-xs bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700">
            <AlertTriangle className="h-3 w-3 mr-1" />
            {(server.critical?.includes('S.M.A.R.T') || server.card?.includes('S.M.A.R.T.')) ? 'S.M.A.R.T!' : 'Критично'}
          </Badge>
        )}
      </div>

      {server.status && (
        <div className="text-xs text-muted-foreground mb-3">
          {parseHtmlToReact(server.status)}
        </div>
      )}

      {/* Collapsible: Network card/netmap data */}
      {server.card && (
        <Collapsible className="mt-2">
          <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground w-full">
            <CollapsibleChevron />
            <Network className="h-3 w-3" />
            Данные о сети
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="text-xs bg-muted/30 rounded p-2 overflow-x-auto mt-1 ml-4">
              {parseHtmlToReact(server.card)}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Collapsible: Connection history log */}
      {server.log && (
        <Collapsible className="mt-2">
          <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground w-full">
            <CollapsibleChevron />
            <History className="h-3 w-3" />
            История разрывов
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="text-xs bg-muted/30 rounded p-2 overflow-x-auto mt-1 ml-4">
              {parseHtmlToReact(server.log)}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}


export function MonitoringTab() {
  const { clientId, detail, detailLoading } = useClientContext();
  const navigate = useNavigate();
  const [bindingSheetOpen, setBindingSheetOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();

  const { data: stationsData, isLoading: stationsLoading, error: stationsError } = useClientStations(clientId);
  const { data: linuxServers, isLoading: linuxLoading, error: linuxError } = useClientCard(clientId);
  const { data: waitingStations } = useWaitingStations(bindingSheetOpen);
  const assignStation = useAssignStation(clientId);

  // Transform stations object to array with display names
  const stations = useMemo<StationWithName[]>(() => {
    if (!stationsData) return [];
    return Object.entries(stationsData).map(([name, station]) => ({
      ...station,
      displayName: name,
    }));
  }, [stationsData]);

  const handleStationClick = (station: StationWithName) => {
    navigate('/more/stations-control/station', {
      state: { station: { ...station, sobid: clientId } },
    });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['legacy', 'stations', clientId] }),
      queryClient.invalidateQueries({ queryKey: ['legacy', 'clientCard', clientId] }),
    ]);
    setIsRefreshing(false);
  };

  const handleAssign = (stationKey: string, comment: string) => {
    assignStation.mutate({ stationKey, comment });
  };

  if (detailLoading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Загрузка...
      </div>
    );
  }

  const client = detail?.client;

  return (
    <div className="p-4 space-y-4">
      {/* Network info (для /etc/network/vars) */}
      {client?.login && (
        <div className="flex gap-3 p-3 rounded-lg bg-muted/50">
          <Network className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="text-xs text-muted-foreground font-mono space-y-0.5">
            <p>ID: <span className="font-semibold text-foreground">{client.id}</span></p>
            <p>LOGIN: <span className="font-semibold text-foreground">{client.login}</span></p>
            <p>TRIAL-{client.id + 2031425000}</p>
            {client.password && (
              <p>PASSWORD: <span className="font-semibold text-foreground">{client.password}</span></p>
            )}
            {client.inn && (
              <p>УНП {client.inn}</p>
            )}
          </div>
        </div>
      )}

      {/* Stations header with actions */}
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-sm flex items-center gap-2">
          <Monitor className="h-4 w-4" />
          Станции контроля ({stations.length})
        </h3>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2"
            onClick={() => setBindingSheetOpen(true)}
          >
            <Link2 className="h-3.5 w-3.5 mr-1" />
            Привязать
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={handleRefresh}
            disabled={isRefreshing || stationsLoading || linuxLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Loading state */}
      {stationsLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      )}

      {/* Error state */}
      {stationsError && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <AlertCircle className="w-8 h-8 text-destructive mb-2" />
          <p className="text-sm text-muted-foreground">Ошибка загрузки станций</p>
        </div>
      )}

      {/* Empty state */}
      {!stationsLoading && !stationsError && stations.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <div className="flex justify-center gap-4 mb-4">
            <Monitor className="h-8 w-8" />
            <Wifi className="h-8 w-8" />
          </div>
          <p className="text-sm">Станций контроля не обнаружено</p>
        </div>
      )}

      {/* Stations list */}
      {!stationsLoading && !stationsError && stations.length > 0 && (
        <div className="space-y-2">
          {stations.map((station) => (
            <StationCard
              key={station.id}
              station={station}
              onClick={() => handleStationClick(station)}
            />
          ))}
        </div>
      )}

      {/* Linux & Netmap section */}
      <div className="flex items-center justify-between mt-6">
        <h3 className="font-medium text-sm flex items-center gap-2">
          <Server className="h-4 w-4" />
          Linux & Netmap ({linuxServers?.length || 0})
        </h3>
      </div>

      {/* Linux loading */}
      {linuxLoading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </div>
      )}

      {/* Linux error */}
      {linuxError && (
        <div className="text-center py-4 text-muted-foreground">
          <p className="text-sm">Ошибка загрузки Linux серверов</p>
        </div>
      )}

      {/* Linux servers list */}
      {!linuxLoading && !linuxError && linuxServers && linuxServers.length > 0 && (
        <div className="space-y-2">
          {linuxServers.map((server, idx) => (
            <LinuxServerCard key={`${server.server}-${idx}`} server={server} />
          ))}
        </div>
      )}

      {/* Empty state for linux */}
      {!linuxLoading && !linuxError && (!linuxServers || linuxServers.length === 0) && (
        <div className="text-center py-4 text-muted-foreground text-xs">
          Нет Linux серверов с Network Mapper
        </div>
      )}

      {/* Station binding sheet */}
      <StationBindingSheet
        open={bindingSheetOpen}
        onOpenChange={setBindingSheetOpen}
        clientName={client?.name || ''}
        waitingStations={waitingStations || []}
        onAssign={handleAssign}
        isAssigning={assignStation.isPending}
      />
    </div>
  );
}
